import { Container, Row, Col, Card } from "react-bootstrap";
import { useSelector } from "react-redux"
import { useEffect, useState } from "react";
import ListUsers from "components/Users/ListUsers";
import NotAuthorized from "./NotAuthorized"
import Menu from "components/general/Menu";
import Footer from "components/general/Footer";
import UsersToolbar from "components/Users/UsersToolbar";
import UsersStatsModal from "components/Users/UsersStatsModal";

export default function UserList() {

  const { user: authUser } = useSelector(x => x.auth)
  const url = process.env.REACT_APP_URL;

  const [userList, setUserList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [toolbarOpen, setToolbarOpen] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);

  const [isLoading, setIsLoading] = useState(true);

  const normalizeAcl = (value) => {
    const normalized = String(value || '').trim().toLowerCase();
    if (normalized === 'officer') return 'Officer';
    if (normalized === 'member') return 'Member';
    if (normalized === 'guest member' || normalized === 'guest_member' || normalized === 'guest-member') return 'Guest Member';
    return 'Guest';
  };

  const looksLikeUser = (entry) => {
    if (!entry || typeof entry !== 'object') return false;
    return Boolean(
      entry._id
      || entry.userId
      || entry.tmAuthUserId
      || entry.email
      || entry.firstName
      || entry.lastName
    );
  };

  const pickUserArray = (candidate) => {
    if (!Array.isArray(candidate)) return null;
    if (candidate.length === 0) return candidate;
    const sample = candidate.slice(0, 5);
    return sample.some(looksLikeUser) ? candidate : null;
  };

  const extractUsersFromPayload = (payload, depth = 0) => {
    if (depth > 4 || payload == null) return [];

    if (typeof payload === 'string') {
      try {
        const parsed = JSON.parse(payload);
        return extractUsersFromPayload(parsed, depth + 1);
      } catch {
        return [];
      }
    }

    const direct = pickUserArray(payload);
    if (direct) return direct;

    const keyCandidates = ['items', 'users', 'data', 'results', 'value', 'payload'];
    for (const key of keyCandidates) {
      const next = payload?.[key];
      const fromKey = pickUserArray(next);
      if (fromKey) return fromKey;
      if (next && typeof next === 'object') {
        const nested = extractUsersFromPayload(next, depth + 1);
        if (nested.length > 0) return nested;
      }
    }

    return [];
  };

  const normalizeUser = (user, index) => {
    const fallbackId = String(user?.userId || user?.tmAuthUserId || user?.id || user?._id || user?.email || `user-${index}`);
    const acl = normalizeAcl(user?.acl);
    return {
      ...user,
      _id: fallbackId,
      userId: String(user?.userId || fallbackId),
      tmAuthUserId: String(user?.tmAuthUserId || user?.userId || fallbackId),
      acl,
      clubRoles: Array.isArray(user?.clubRoles) ? user.clubRoles : [],
      firstName: String(user?.firstName || ''),
      lastName: String(user?.lastName || ''),
    };
  };


  const getUsers = async () => {
    setIsLoading(true);
    try {
      const authToken = String(authUser?.token || authUser?.user?.token || '').trim();
      const response = await fetch(`${url}/users/`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`(${response.status}) We could not retrieve the users`);
      }

      const resData = await response.json();
      const users = extractUsersFromPayload(resData).map(normalizeUser);

      const roleOrder = {
        "Officer": 1,
        "Member": 2,
        "Guest Member": 3,
        "Guest": 4,
      };

      const sortedUserList = users
        .sort((a, b) => {
          const roleComparison = (roleOrder[normalizeAcl(a?.acl)] || 99) - (roleOrder[normalizeAcl(b?.acl)] || 99);
          if (roleComparison === 0) {
            const aFirstName = String(a?.firstName || '');
            const bFirstName = String(b?.firstName || '');
            return aFirstName.localeCompare(bFirstName);
          }
          return roleComparison;
        });

      console.log(`[Users page] Received ${users.length} users, rendering ${sortedUserList.length} users after normalization`);
      if (users.length === 0) {
        console.log('[Users page] Payload keys:', Object.keys(resData || {}));
      }

      setUserList(sortedUserList);
    } catch (error) {
      console.error("Error fetching users:", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getUsers();
    // eslint-disable-next-line
  }, []);

  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };

  const handleRoleFilterChange = (role) => {
    setSelectedRole(role);
  };

  const filteredUsers = userList.filter((user) => {
    const firstName = String(user?.firstName || '').toLowerCase();
    const lastName = String(user?.lastName || '').toLowerCase();
    const acl = normalizeAcl(user?.acl);
    const matchesSearchTerm =
      firstName.includes(searchTerm.toLowerCase()) ||
      lastName.includes(searchTerm.toLowerCase());
    const matchesRole = selectedRole ? acl === normalizeAcl(selectedRole) : true;
    return matchesSearchTerm && matchesRole;
  });


  if (!authUser) {
    return <NotAuthorized />
  }
  else {
    return (
      <section className="main users-page">
        <Menu />

        <Container className="users-page-container">
          <Row className='mt-2'>
            <Col xs={12} lg={12}>
              <Card className='meetings-title-card'>
                <Card.Body>
                  <h3 className='meetings-title-card-text'>Users</h3>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <div className="users-page-scroll">
            <ListUsers userList={filteredUsers} searchTerm={searchTerm} selectedRole={selectedRole} isLoading={isLoading} />
          </div>
        </Container>
        <Footer />
        <UsersToolbar
          menuOpen={toolbarOpen}
          setMenuOpen={setToolbarOpen}
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          selectedRole={selectedRole}
          onRoleChange={handleRoleFilterChange}
          getUsers={getUsers}
          onOpenStats={() => setShowStatsModal(true)}
        />
        <UsersStatsModal
          navigationParent="configuration"
          show={showStatsModal}
          handleClose={() => setShowStatsModal(false)}
          userList={userList}
        />
      </section>
    )
  }
}